import { createFileRoute, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Copy, MessageCircleQuestion, Megaphone, RefreshCw, Trash2, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  answerClassPost,
  deleteClassPost,
  enrollStudent,
  getClassDetail,
  leaveClass,
  removeStudent,
  postToClassBoard,
  rotateJoinCode,
  updateClassAnnouncement,
  updateClass,
} from "@/lib/teacher.functions";
import { ClassAssignments } from "@/components/ClassAssignments";
import { ClassChallenges } from "@/components/ClassChallenges";
import { ClassAnalytics } from "@/components/ClassAnalytics";
import { ClassReports } from "@/components/ClassReports";
import { ClassStudentProgress } from "@/components/ClassStudentProgress";
import { ClassSyllabus } from "@/components/ClassSyllabus";
import { ClassMaterialsTab } from "@/components/ClassMaterialsTab";
import { ClassCalendarTab } from "@/components/ClassCalendarTab";
import { ClassProgressDashboard } from "@/components/ClassProgressDashboard";
import { RecoverySuggestions } from "@/components/RecoverySuggestions";
import { ClassReportExport } from "@/components/ClassReportExport";
import { MyClassProgress } from "@/components/MyClassProgress";

export const Route = createFileRoute("/_app/class/$classId")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Classe virtuale — Lumen" },
      { name: "description", content: "Bacheca, studenti e impostazioni della tua classe virtuale su Lumen." },
      { property: "og:title", content: "Classe virtuale — Lumen" },
      { property: "og:description", content: "Bacheca, studenti e impostazioni della classe." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ClassPage,
});

type Post = {
  id: string;
  kind: "announcement" | "question";
  body: string;
  is_anonymous: boolean;
  answered: boolean;
  answer: string | null;
  author_id: string | null;
  created_at: string;
  status?: "draft" | "scheduled" | "published";
  publish_at?: string | null;
};

type Detail = Awaited<ReturnType<typeof getClassDetail>>;

function ClassPage() {
  const { classId } = useParams({ from: "/_app/class/$classId" });
  const { t, i18n } = useTranslation();
  const detailFn = useServerFn(getClassDetail);
  const postFn = useServerFn(postToClassBoard);
  const answerFn = useServerFn(answerClassPost);
  const deleteFn = useServerFn(deleteClassPost);
  const rotateFn = useServerFn(rotateJoinCode);
  const updateFn = useServerFn(updateClass);
  const leaveFn = useServerFn(leaveClass);
  const updatePostFn = useServerFn(updateClassAnnouncement);
  const enrollFn = useServerFn(enrollStudent);
  const removeFn = useServerFn(removeStudent);

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<"published" | "draft" | "scheduled">("published");
  const [publishAt, setPublishAt] = useState("");
  const [preview, setPreview] = useState(false);
  // Iscrizione diretta degli studenti da parte del docente (senza codice).
  const [newStudent, setNewStudent] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await detailFn({ data: { classId } }));
    } catch {
      toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <main className="px-4 py-6">
        <div className="h-32 animate-pulse rounded-3xl bg-muted/50" />
      </main>
    );
  }
  if (!data) return null;

  const { isTeacher, klass, members } = data;
  const posts = data.posts as Post[];
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: "2-digit", month: "short" });

  const submitPost = async () => {
    if (!body.trim()) return;
    if (isTeacher && mode === "scheduled" && !publishAt) {
      toast.error(t("teacher.board.needDate"));
      return;
    }
    await postFn({
      data: {
        classId,
        kind: isTeacher ? "announcement" : "question",
        body: body.trim(),
        is_anonymous: anonymous,
        ...(isTeacher
          ? {
              status: mode,
              publishAt: mode === "scheduled" ? new Date(publishAt).toISOString() : null,
            }
          : {}),
      },
    });
    setBody("");
    setPreview(false);
    setPublishAt("");
    toast.success(isTeacher ? t("teacher.board.publish") : t("teacher.board.sent"));
    void load();
  };

  return (
    <main className="space-y-5 px-4 py-6">
      <header className="space-y-1">
        <h1 className="break-words text-2xl font-bold tracking-tight">{klass.name}</h1>
        <p className="break-words text-sm text-muted-foreground">{klass.subject}</p>
      </header>

      <Tabs defaultValue="board" className="w-full">
        <div className="-mx-4 overflow-x-auto px-4 pb-1">
          <TabsList className="inline-flex w-max min-w-full">
            <TabsTrigger value="board" className="shrink-0">
              {t("teacher.tabs.board")}
            </TabsTrigger>
            <TabsTrigger value="syllabus" className="shrink-0">
              {t("teacher.tabs.syllabus")}
            </TabsTrigger>
            <TabsTrigger value="materials" className="shrink-0">
              {t("teacher.tabs.materials")}
            </TabsTrigger>
            <TabsTrigger value="work" className="shrink-0">
              {t("teacher.tabs.work")}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="shrink-0">
              {t("teacher.tabs.calendar")}
            </TabsTrigger>
            <TabsTrigger value="challenges" className="shrink-0">
              {t("teacher.tabs.challenges")}
            </TabsTrigger>
            <TabsTrigger value="progress" className="shrink-0">
              {t("progress.tab")}
            </TabsTrigger>
            {isTeacher && (
              <>
                <TabsTrigger value="students" className="shrink-0">
                  {t("teacher.tabs.progress")}
                </TabsTrigger>
                <TabsTrigger value="settings" className="shrink-0">
                  {t("teacher.tabs.settings")}
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        <TabsContent value="syllabus" className="space-y-3 pt-3">
          <ClassSyllabus classId={classId} isTeacher={isTeacher} />
        </TabsContent>

        <TabsContent value="materials" className="space-y-3 pt-3">
          <ClassMaterialsTab classId={classId} isTeacher={isTeacher} />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-3 pt-3">
          <ClassCalendarTab classId={classId} />
        </TabsContent>

        <TabsContent value="challenges" className="space-y-3 pt-3">
          <ClassChallenges classId={classId} isTeacher={isTeacher} />
        </TabsContent>

        <TabsContent value="work" className="space-y-3 pt-3">
          <ClassAssignments classId={classId} isTeacher={isTeacher} />
        </TabsContent>


        <TabsContent value="board" className="space-y-3 pt-3">
          <Card className="card-soft space-y-3 border-0 p-4">
            <Label htmlFor="post-body" className="text-sm font-semibold">
              {isTeacher ? t("teacher.board.write") : t("teacher.board.ask")}
            </Label>
            <Textarea
              id="post-body"
              rows={3}
              value={body}
              placeholder={isTeacher ? "" : t("teacher.board.askPh")}
              onChange={(e) => setBody(e.target.value)}
            />
            {isTeacher && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {(["published", "draft", "scheduled"] as const).map((m) => (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={mode === m ? "default" : "secondary"}
                      className="min-h-9 rounded-full"
                      onClick={() => setMode(m)}
                    >
                      {t(`teacher.board.mode.${m}`)}
                    </Button>
                  ))}
                </div>
                {mode === "scheduled" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="publish-at" className="text-xs text-muted-foreground">
                      {t("teacher.board.scheduleAt")}
                    </Label>
                    <Input
                      id="publish-at"
                      type="datetime-local"
                      value={publishAt}
                      onChange={(e) => setPublishAt(e.target.value)}
                    />
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="preview" className="text-xs text-muted-foreground">
                    {t("teacher.board.preview")}
                  </Label>
                  <Switch id="preview" checked={preview} onCheckedChange={setPreview} />
                </div>
                {preview && body.trim() && (
                  <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("teacher.board.previewTitle")}
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{body}</p>
                  </div>
                )}
              </div>
            )}
            {!isTeacher && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="anon" className="text-xs text-muted-foreground">
                  {t("teacher.board.anonymous")}
                </Label>
                <Switch id="anon" checked={anonymous} onCheckedChange={setAnonymous} />
              </div>
            )}
            <Button className="min-h-11 w-full" disabled={!body.trim()} onClick={submitPost}>
              {isTeacher ? t(`teacher.board.submit.${mode}`) : t("teacher.board.send")}
            </Button>
          </Card>

          {posts.length === 0 ? (
            <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("teacher.board.empty")}</Card>
          ) : (
            posts.map((p) => (
              <Card key={p.id} className="card-soft space-y-2 border-0 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={p.kind === "question" ? "secondary" : "default"} className="shrink-0">
                    {p.kind === "question" ? (
                      <MessageCircleQuestion className="mr-1 h-3 w-3" aria-hidden />
                    ) : (
                      <Megaphone className="mr-1 h-3 w-3" aria-hidden />
                    )}
                    {t(`teacher.board.${p.kind}`)}
                  </Badge>
                  {isTeacher && p.status && p.status !== "published" && (
                    <Badge variant="outline" className="shrink-0">
                      {p.status === "draft"
                        ? t("teacher.board.mode.draft")
                        : `${t("teacher.board.mode.scheduled")} · ${p.publish_at ? new Date(p.publish_at).toLocaleString(i18n.language) : "—"}`}
                    </Badge>
                  )}
                  {p.is_anonymous && (
                    <Badge variant="outline" className="shrink-0">
                      {t("teacher.board.anonymousBadge")}
                    </Badge>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground">{fmt(p.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{p.body}</p>
                {p.answered && p.answer && (
                  <div className="rounded-2xl bg-muted/60 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("teacher.board.answered")}
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{p.answer}</p>
                  </div>
                )}
                {isTeacher && p.kind === "question" && !p.answered && (
                  <div className="space-y-2">
                    <Textarea
                      rows={2}
                      value={answers[p.id] ?? ""}
                      onChange={(e) => setAnswers({ ...answers, [p.id]: e.target.value })}
                    />
                    <Button
                      size="sm"
                      className="min-h-10"
                      disabled={!(answers[p.id] ?? "").trim()}
                      onClick={async () => {
                        await answerFn({ data: { postId: p.id, answer: answers[p.id].trim() } });
                        setAnswers({ ...answers, [p.id]: "" });
                        void load();
                      }}
                    >
                      {t("teacher.board.answer")}
                    </Button>
                  </div>
                )}
                {isTeacher && p.status && p.status !== "published" && (
                  <Button
                    size="sm"
                    className="min-h-9"
                    onClick={async () => {
                      await updatePostFn({ data: { postId: p.id, status: "published" } });
                      toast.success(t("teacher.board.publishedNow"));
                      void load();
                    }}
                  >
                    <Megaphone className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("teacher.board.publishNow")}
                  </Button>
                )}
                {isTeacher && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-9 text-destructive"
                    onClick={async () => {
                      await deleteFn({ data: { postId: p.id } });
                      void load();
                    }}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("teacher.board.delete")}
                  </Button>
                )}
              </Card>
            ))
          )}

          {!isTeacher && (
            <Button
              variant="ghost"
              className="min-h-10 w-full text-destructive"
              onClick={async () => {
                await leaveFn({ data: { classId } });
                toast.success(t("teacher.join.left"));
                history.back();
              }}
            >
              {t("teacher.join.leave")}
            </Button>
          )}
        </TabsContent>

        <TabsContent value="progress" className="space-y-3 pt-3">
          {isTeacher ? (
            <>
              <ClassProgressDashboard classId={classId} />
              <RecoverySuggestions classId={classId} />
              <ClassReportExport classId={classId} className={klass?.name ?? ""} />
            </>
          ) : (
            <MyClassProgress classId={classId} />
          )}
        </TabsContent>

        {isTeacher && (
          <>




          <TabsContent value="students" className="space-y-3 pt-3">
            <ClassAnalytics classId={classId} />
            <ClassReports classId={classId} className={klass?.name ?? ""} />
            <ClassStudentProgress classId={classId} />

            <Card className="card-soft space-y-3 border-0 p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4" aria-hidden />
                {t("teacher.classes.studentsTitle")}
              </h2>

              {/* Il docente iscrive direttamente: nessun codice da chiedere. */}
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const handle = newStudent.trim();
                  if (!handle || enrolling) return;
                  setEnrolling(true);
                  try {
                    const res = await enrollFn({ data: { classId, username: handle } });
                    if (!res.ok) {
                      toast.error(
                        res.reason === "not_found"
                          ? t("teacher.classes.addStudentNotFound", {
                              defaultValue: "Nessuno studente trovato con questo nome, username o email.",
                            })
                          : res.reason === "ambiguous"
                            ? t("teacher.classes.addStudentAmbiguous", {
                                defaultValue:
                                  "Più studenti corrispondono: usa l'email o lo username per essere preciso.",
                              })
                            : t("states.error.title", { defaultValue: "Something went wrong" }),
                      );
                      return;
                    }
                    setNewStudent("");
                    toast.success(
                      t("teacher.classes.addStudentDone", { defaultValue: "Studente iscritto alla classe." }),
                    );
                    await load();
                  } catch {
                    toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
                  } finally {
                    setEnrolling(false);
                  }
                }}
              >
                <div className="min-w-[200px] flex-1 space-y-1">
                  <Label htmlFor="add-student" className="text-xs text-muted-foreground">
                    {t("teacher.classes.addStudentLabel", {
                      defaultValue: "Aggiungi studente (nome, username o email)",
                    })}
                  </Label>
                  <Input
                    id="add-student"
                    value={newStudent}
                    onChange={(e) => setNewStudent(e.target.value)}
                    placeholder={t("teacher.classes.addStudentPh", {
                      defaultValue: "Nome completo, @username o email",
                    })}
                    autoComplete="off"
                  />
                </div>
                <Button type="submit" disabled={enrolling || !newStudent.trim()}>
                  {t("teacher.classes.addStudentCta", { defaultValue: "Iscrivi" })}
                </Button>
              </form>

              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("teacher.classes.studentsEmpty")}</p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {members.map((m) => (
                    <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <span className="min-w-0 break-words text-sm font-medium">{m.name}</span>
                      <span className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
                        {t("teacher.classes.joined", { date: fmt(m.joined_at) })}
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={t("teacher.classes.removeStudent", {
                            defaultValue: "Rimuovi dalla classe",
                          })}
                          onClick={async () => {
                            try {
                              await removeFn({ data: { classId, userId: m.user_id } });
                              await load();
                            } catch {
                              toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </TabsContent>
          </>
        )}


        {isTeacher && (
          <TabsContent value="settings" className="space-y-3 pt-3">
            <Card className="card-soft space-y-4 border-0 p-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t("teacher.classes.code")}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded-lg bg-muted px-3 py-1.5 text-sm font-semibold tracking-widest">
                    {klass.join_code}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-9"
                    onClick={() => {
                      navigator.clipboard?.writeText(klass.join_code);
                      toast.success(t("teacher.classes.copied"));
                    }}
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("teacher.classes.copyCode")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-9"
                    onClick={async () => {
                      const r = await rotateFn({ data: { id: classId } });
                      setData({ ...data, klass: { ...klass, join_code: r.join_code } });
                      toast.success(t("teacher.classes.rotated"));
                    }}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("teacher.classes.rotate")}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="invite" className="text-sm">
                  {t("teacher.classes.inviteOpen")}
                </Label>
                <Switch
                  id="invite"
                  checked={klass.invite_enabled}
                  onCheckedChange={async (v) => {
                    setData({ ...data, klass: { ...klass, invite_enabled: v } });
                    await updateFn({ data: { id: classId, invite_enabled: v } });
                  }}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="lb" className="text-sm">
                    {t("teacher.classes.leaderboard")}
                  </Label>
                  <Switch
                    id="lb"
                    checked={klass.leaderboard_enabled}
                    onCheckedChange={async (v) => {
                      setData({ ...data, klass: { ...klass, leaderboard_enabled: v } });
                      await updateFn({ data: { id: classId, leaderboard_enabled: v } });
                    }}
                  />
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {t("teacher.classes.leaderboardHint")}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cl-name">{t("teacher.classes.name")}</Label>
                <Input
                  id="cl-name"
                  defaultValue={klass.name}
                  onBlur={async (e) => {
                    const name = e.target.value.trim();
                    if (name && name !== klass.name) {
                      await updateFn({ data: { id: classId, name } });
                      void load();
                    }
                  }}
                />
              </div>

              <p className="text-[11px] leading-relaxed text-muted-foreground">{t("teacher.privacy.note")}</p>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </main>
  );
}
